/**
 * How long a password must be (shared by the API, the admin web and Dob Desktop).
 *
 * - Everyone: at least 8 characters (plus the checks in `@dob/security` passwordProblems: not a
 *   common password, not the phone number).
 * - Owner rule D147: a **management account that may sign in without internet** — a role that
 *   requires 2FA plus a role on the offline allow-list somewhere, e.g. IT and branch managers,
 *   whose offline copies are kept on branch hubs — needs at least 16 characters. The database's
 *   `user_needs_long_password` (0014) decides who that is on the server; devices apply the same
 *   rule to what their own copy of the data shows.
 */
import type { LocalizedText } from '../errors/errors.ts';

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
/** D147: management accounts whose offline copies are kept on hubs. */
export const LONG_PASSWORD_MIN_LENGTH = 16;

/** Why a person's password must be longer than usual (null: the general rule). */
export type PasswordRuleReason = 'offline_management';

export interface PasswordRule {
  readonly minLength: number;
  readonly reason: PasswordRuleReason | null;
}

export function passwordRule(needsLong: boolean): PasswordRule {
  return needsLong
    ? { minLength: LONG_PASSWORD_MIN_LENGTH, reason: 'offline_management' }
    : { minLength: PASSWORD_MIN_LENGTH, reason: null };
}

/**
 * Characters, as a person counts them: Unicode code points after NFC normalization (the form
 * the hash is made from), never UTF-16 units — an Arabic letter or an emoji is one character.
 */
export function passwordLength(password: string): number {
  return Array.from(password.normalize('NFC')).length;
}

/** Long enough for a management account that may sign in without internet (D147). */
export function isLongPassword(password: string): boolean {
  return passwordLength(password) >= LONG_PASSWORD_MIN_LENGTH;
}

/** The refusal shown under the new-password field when the D147 rule is not met. */
export const LONG_PASSWORD_REQUIRED: LocalizedText = {
  ar: `حسابك إداري ويمكنه الدخول بدون إنترنت على أجهزة الفروع، لذلك يجب أن تكون كلمة المرور ${String(LONG_PASSWORD_MIN_LENGTH)} حرفاً على الأقل.`,
  en: `Your account is a management account that can sign in without internet on branch PCs, so its password must be at least ${String(LONG_PASSWORD_MIN_LENGTH)} characters.`,
};
